import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarCheck, Cake, Check, MapPin } from "lucide-react";
import { WhatsAppGlyph } from "@/components/cards/CardShell";
import { waLink } from "@/lib/wa";
import { fetchAgenda } from "@/lib/bf";

const Row = ({ x, children }) => (
  <li data-testid={`bf-agenda-row-${x.id}`}>
    <Link to={`/client/${x.id}`}><b>{x.salon || "Salon"}</b><small>{[x.address, x.city].filter(Boolean).join(", ")}</small></Link>
    {children}
  </li>
);

const MAX = 9;
const place = (x) => [x.address, x.city, "România"].filter(Boolean).join(", ");
const routes = (list) => {
  const out = [];
  for (let i = 0; i < list.length; i += MAX + 1) {
    const part = list.slice(i, i + MAX + 1);
    const dest = part[part.length - 1];
    const way = part.slice(0, -1).map(place).join("|");
    out.push({ from: i + 1, to: i + part.length, url: `https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=${encodeURIComponent(place(dest))}${way ? `&waypoints=${encodeURIComponent(way)}` : ""}` });
  }
  return out;
};

const MapBtns = ({ visit }) => {
  const list = visit.filter((x) => !x.done && (x.address || x.city));
  if (!list.length) return null;
  const rs = routes(list);
  return (
    <div className="share-actions bf-agenda-map">
      {rs.map((r, i) => (
        <a key={r.from} className="st-btn st-btn--light" href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`bf-agenda-map-${i}`}>
          <MapPin size={14} /> {rs.length > 1 ? `Traseu ${r.from}–${r.to} în Google Maps` : `Traseu în Google Maps (${list.length} ${list.length === 1 ? "salon" : "saloane"})`}
        </a>
      ))}
    </div>
  );
};

export default function BFAgenda({ empty = false }) {
  const [a, setA] = useState(null);
  useEffect(() => { const load = () => fetchAgenda().then(setA).catch(() => {}); load(); window.addEventListener("bf-plan-changed", load); return () => window.removeEventListener("bf-plan-changed", load); }, []);
  if (!a) return null;
  if (!(a.visit.length || a.birthdays.length || a.neglected.length)) return empty ? <p className="st-hint" data-testid="bf-agenda-empty">Azi nu ai nicio vizită planificată, nicio zi de naștere și niciun salon neglijat.</p> : null;
  return (
    <div className="bf-agenda" data-testid="bf-agenda">
      {a.visit.length > 0 && <section data-testid="bf-agenda-visit">
        <div className="st-section-title"><CalendarCheck size={12} /> Azi de vizitat · {a.day} ({a.visit.length})</div>
        <ul>{a.visit.map((x) => <Row key={x.id} x={x}><span className={x.done ? "is-done" : ""}>{x.done ? <><Check size={13} /> vizitat azi</> : x.best_time || (x.last_visit ? `ultima vizită acum ${x.days_ago} zile` : "încă nevizitat")}</span></Row>)}</ul>
        <MapBtns visit={a.visit} />
      </section>}
      {a.birthdays.length > 0 && <section data-testid="bf-agenda-bdays">
        <div className="st-section-title"><Cake size={12} /> Zile de naștere</div>
        <ul>{a.birthdays.map((x) => (
          <Row key={x.id} x={x}>
            <span><b>{x.admin_name || "Administratorul"}</b> · {x.when}</span>
            {waLink(x.phone, x.message) && <a className="st-btn st-btn--light" href={waLink(x.phone, x.message)} target="_blank" rel="noopener noreferrer" data-testid={`bf-agenda-bday-wa-${x.id}`}><WhatsAppGlyph /> Trimite felicitarea</a>}
          </Row>
        ))}</ul>
      </section>}
      {a.neglected.length > 0 && <section className="is-red" data-testid="bf-agenda-neglected">
        <div className="st-section-title"><AlertTriangle size={12} /> Nevizitate de peste o lună ({a.neglected.length})</div>
        <ul>{a.neglected.map((x) => <Row key={x.id} x={x}><span>acum {x.days_ago} zile</span></Row>)}</ul>
      </section>}
    </div>
  );
}
